import { APIComponent, APIProject, APIError, HealthResponse, APISchema } from '../types/api';

// Simple schema validation functions
export class SchemaValidator {
  private schema: APISchema | null = null;

  async loadSchema(schemaUrl: string): Promise<void> {
    try {
      const response = await fetch(schemaUrl);
      if (!response.ok) {
        throw new Error(`Failed to load schema: ${response.statusText}`);
      }
      this.schema = await response.json();
    } catch (error) {
      console.error('Error loading schema:', error);
      throw error;
    }
  }

  // Validate Component
  validateComponent(data: any): data is APIComponent {
    if (!data || typeof data !== 'object') {
      console.error('Component validation failed: not an object');
      return false;
    }

    // Required fields
    if (typeof data.name !== 'string' || !data.name) {
      console.error('Component validation failed: missing or invalid name');
      return false;
    }

    // Make path optional for now since production API might not provide it
    if (data.path !== undefined && (typeof data.path !== 'string' || !data.path)) {
      console.error('Component validation failed: path must be a string if provided');
      return false;
    }

    // Optional fields validation
    if (data.template !== undefined && typeof data.template !== 'string') {
      console.error('Component validation failed: template must be a string');
      return false;
    }

    if (data.scss !== undefined && typeof data.scss !== 'string') {
      console.error('Component validation failed: scss must be a string');
      return false;
    }

    if (data.js !== undefined && typeof data.js !== 'string') {
      console.error('Component validation failed: js must be a string');
      return false;
    }

    // Meta validation
    if (data.meta !== undefined) {
      if (typeof data.meta !== 'object' || data.meta === null) {
        console.error('Component validation failed: meta must be an object');
        return false;
      }

      if (data.meta.title !== undefined && typeof data.meta.title !== 'string') {
        console.error('Component validation failed: meta.title must be a string');
        return false;
      }

      if (data.meta.description !== undefined && typeof data.meta.description !== 'string') {
        console.error('Component validation failed: meta.description must be a string');
        return false;
      }

      if (data.meta.category !== undefined && typeof data.meta.category !== 'string') {
        console.error('Component validation failed: meta.category must be a string');
        return false;
      }

      if (data.meta.tags !== undefined) {
        if (!Array.isArray(data.meta.tags)) {
          console.error('Component validation failed: meta.tags must be an array');
          return false;
        }
        if (!data.meta.tags.every((tag: any) => typeof tag === 'string')) {
          console.error('Component validation failed: meta.tags must contain only strings');
          return false;
        }
      }

      if (data.meta.status !== undefined) {
        const validStatuses = ['ready', 'in-progress', 'deprecated'];
        if (!validStatuses.includes(data.meta.status)) {
          console.error('Component validation failed: meta.status must be one of:', validStatuses);
          return false;
        }
      }
    }

    // Variants validation
    if (data.variants !== undefined) {
      if (!Array.isArray(data.variants)) {
        console.error('Component validation failed: variants must be an array');
        return false;
      }
      
      for (const variant of data.variants) {
        if (typeof variant !== 'object' || variant === null) {
          console.error('Component validation failed: variant must be an object');
          return false;
        }
        if (typeof variant.name !== 'string' || !variant.name) {
          console.error('Component validation failed: variant must have a valid name');
          return false;
        }
      }
    }

    return true;
  }

  // Validate Project
  validateProject(data: any): data is APIProject {
    if (!data || typeof data !== 'object') {
      console.error('Project validation failed: not an object');
      return false;
    }

    // Required fields
    if (typeof data.id !== 'string' || !data.id) {
      console.error('Project validation failed: missing or invalid id');
      return false;
    }

    if (typeof data.name !== 'string' || !data.name) {
      console.error('Project validation failed: missing or invalid name');
      return false;
    }

    // Optional fields validation
    if (data.description !== undefined && typeof data.description !== 'string') {
      console.error('Project validation failed: description must be a string');
      return false;
    }

    if (data.version !== undefined && typeof data.version !== 'string') {
      console.error('Project validation failed: version must be a string');
      return false;
    }

    if (data.componentsCount !== undefined && typeof data.componentsCount !== 'number') {
      console.error('Project validation failed: componentsCount must be a number');
      return false;
    }

    if (data.lastUpdated !== undefined && typeof data.lastUpdated !== 'string') {
      console.error('Project validation failed: lastUpdated must be a string');
      return false;
    }

    return true;
  }

  // Validate API Error
  validateAPIError(data: any): data is APIError {
    if (!data || typeof data !== 'object') {
      console.error('APIError validation failed: not an object');
      return false;
    }

    if (typeof data.error !== 'string' || !data.error) {
      console.error('APIError validation failed: missing or invalid error');
      return false;
    }

    if (typeof data.message !== 'string' || !data.message) {
      console.error('APIError validation failed: missing or invalid message');
      return false;
    }

    if (data.timestamp !== undefined && typeof data.timestamp !== 'string') {
      console.error('APIError validation failed: timestamp must be a string');
      return false;
    }

    return true;
  }

  // Validate Health Response
  validateHealthResponse(data: any): data is HealthResponse {
    if (!data || typeof data !== 'object') {
      console.error('HealthResponse validation failed: not an object');
      return false;
    }

    if (typeof data.status !== 'string' || !['healthy', 'unhealthy'].includes(data.status)) {
      console.error('HealthResponse validation failed: status must be "healthy" or "unhealthy"');
      return false;
    }

    if (data.timestamp !== undefined && typeof data.timestamp !== 'string') {
      console.error('HealthResponse validation failed: timestamp must be a string');
      return false;
    }

    if (data.version !== undefined && typeof data.version !== 'string') {
      console.error('HealthResponse validation failed: version must be a string');
      return false;
    }

    return true;
  }

  // Validate array of components
  validateComponentsArray(data: any): data is APIComponent[] {
    if (!Array.isArray(data)) {
      console.error('Components array validation failed: not an array');
      return false;
    }

    for (let i = 0; i < data.length; i++) {
      if (!this.validateComponent(data[i])) {
        console.error(`Components array validation failed: invalid component at index ${i}`);
        return false;
      }
    }

    return true;
  }

  // Validate array of projects
  validateProjectsArray(data: any): data is APIProject[] {
    if (!Array.isArray(data)) {
      console.error('Projects array validation failed: not an array');
      return false;
    }

    for (let i = 0; i < data.length; i++) {
      if (!this.validateProject(data[i])) {
        console.error(`Projects array validation failed: invalid project at index ${i}`);
        return false;
      }
    }

    return true;
  }
}

// Create a singleton instance
export const schemaValidator = new SchemaValidator();
